// Angular
import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
// PrimeNG
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SidebarModule } from 'primeng/sidebar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
// Models
import { ISession } from '@core/models-v2/auth/session.interface';
import { AddressType } from '@core/enums/address-type.enum';
import { ICustomerAddress } from '@core/models-v2/customer/customer.interface';
import { IShoppingCart } from '@core/models-v2/cart/shopping-cart.interface';
import { AddressOption } from '../delivery-address-form/models/address-option.type';
// Services
import { CustomerAddressApiService } from '@core/services-v2/customer-address/customer-address-api.service';
import { CustomerAddressService } from '@core/services-v2/customer-address/customer-address.service';
import { SessionService } from '@core/services-v2/session/session.service';
import { CustomerService } from '@core/services-v2/customer.service';
import { CartService } from '@core/services-v2/cart.service';
import { getAddressOptions } from '../delivery-address-form/utils/address.utils';
import { BlockUiService } from '@core/services-v2/block-ui/block-ui.service';
// Components
import {
  AddressForm,
  AddressFormControlContainerComponent,
  AddressFormInit,
  //AddressFormInitCL,
} from '@shared/components-v3/form-containers/address-form-control-container/address-form-control-container.component';
// Directives
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';

import { environment } from '@env/environment';
import { CreateGuestRequestDto } from '@core/services-v2/customer/dtos/guest/create-guest-request.dto';
import { CustomerApiService } from '@core/services-v2/customer/customer-api.service';
import { ControlsOf } from '@shared/models-v3/controls-of.type';
import { CheckoutStorageService } from 'src/app/checkout-v2/services/checkout-storage/checkout-storage.service';

const NG_MODULES = [ReactiveFormsModule, NgClass, FormsModule];
const PRIME_MODULES = [
  SidebarModule,
  ButtonModule,
  RadioButtonModule,
  ProgressSpinnerModule,
  ToastModule,
  ConfirmDialogModule,
];
const COMPONENTS = [AddressFormControlContainerComponent];
const DIRECTIVES = [ControlErrorsDirective, FormSubmitDirective];

@Component({
  selector: 'app-address-list-sidebar',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, COMPONENTS, DIRECTIVES],
  templateUrl: './address-list-sidebar.component.html',
  styleUrl: './address-list-sidebar.component.scss',
  providers: [ConfirmationService, MessageService],
})
export class AddressListSidebarComponent {
  //#region ViewChild elements
  @ViewChild('addressFormElement')
  addressFormElement!: ElementRef<HTMLFormElement>;
  //#endregion

  //#region Inputs & outputs
  @Input() addresses: AddressOption[] = [];
  @Input() selectedAddressId: string | null = null;
  @Input() parentForm!: FormGroup;
  @Output() onSubmit: EventEmitter<AddressOption | null> = new EventEmitter();
  //#endregion

  //#region Forms
  selectedAddressForm!: FormGroup<ControlsOf<{ id: string | null }>>;
  addressForm!: FormGroup<ControlsOf<Partial<AddressFormInit>>>;
  //#endregion

  step = signal<number>(1);
  cartSession!: IShoppingCart;
  visible: boolean = false;
  isLoggedIn: boolean = false;
  session!: ISession;
  countryCode = environment.country;

  //#region Getters
  get idField(): FormControl<string | null> {
    return this.selectedAddressForm.controls.id;
  }
  //#endregion

  //#region Dependencies
  private readonly fb = inject(FormBuilder);
  private readonly customerAddressService = inject(CustomerAddressService);
  private readonly customerService = inject(CustomerService);
  private readonly customerApiService = inject(CustomerApiService);
  private readonly customerAddressApiService = inject(
    CustomerAddressApiService
  );
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  public readonly sessionService = inject(SessionService);
  public readonly cart = inject(CartService);
  public readonly blockUiService = inject(BlockUiService);
  private readonly checkoutStorageService = inject(CheckoutStorageService);
  //#endregion

  //#region Lyfecycle hooks
  constructor() {
    this.buildForms();
    this.session = this.sessionService.getSession();
    this.isLoggedIn = this.sessionService.isLoggedIn();
  }
  //#endregion

  private buildForms(): void {
    this.selectedAddressForm = this.fb.group({
      id: new FormControl<string | null>(null),
    });
    this.addressForm = this.fb.group({});
  }

  /**
   * Mostrar el sidebar.
   * Se ejecuta desde el componente padre.
   * @returns void
   */
  show(): void {
    this.visible = true;
  }

  /**
   * Al mostrar el sidebar setea que contenido mostrar [OK].
   */
  onShow(): void {
    if (!this.isLoggedIn || !this.addresses.length) this.step.set(2);
    else if (this.selectedAddressId) {
      this.selectedAddressForm.setValue({
        id: this.selectedAddressId,
      });

      const selectedAddress = this.addresses.find(
        (address) => address.id === this.selectedAddressId
      );
      if (selectedAddress) {
        this.addresses = [
          selectedAddress,
          ...this.addresses.filter(
            (address) => address.id !== this.selectedAddressId
          ),
        ];
      }
    }
  }

  /**
   * Al ocultar el sidebar.
   */
  onHide(): void {
    this.step.set(1);
    const isSelectedAddressDeleted = !this.addresses.some(
      (address) => address.id === this.selectedAddressId
    );
    if (isSelectedAddressDeleted) this.onSubmit.emit(null);
  }

  /**
   * Al seleccionar dirección.
   * @param index - Índice de la dirección seleccionada.
   */
  onSelectAddress(index: number): void {
    const selectedAddress = this.addresses[index];
    this.selectedAddressForm.patchValue({
      id: selectedAddress.id,
    });
  }

  /**
   * Eliminar dirección.
   * @param index - Índice de la dirección a eliminar.
   */
  private deleteAddress(index: number): void {
    this.blockUiService.set(true);
    const { id } = this.addresses[index];
    this.customerAddressApiService
      .deleteAddress(this.session.documentId, id)
      .subscribe({
        next: () => {
          this.addresses.splice(index, 1);
          if (this.selectedAddressId === id) this.selectedAddressForm.reset();
          this.blockUiService.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Dirección eliminada',
            detail: 'La dirección ha sido eliminada correctamente.',
          });
        },
        error: () => {
          this.blockUiService.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error eliminando dirección',
            detail: 'Ha ocurrido un error eliminando la dirección.',
          });
        },
      });
  }

  /**
   * Confirmar eliminación de dirección.
   * @param event - Evento del botón.
   * @param index - Índice de la dirección a eliminar.
   */
  confirmDeleteAddress(event: Event, index: number): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Estas seguro que deseas eliminar esta dirección?',
      header: 'Eliminar dirección',
      icon: 'pi pi-info-circle',
      rejectLabel: 'Cancelar',
      acceptLabel: 'Eliminar',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      accept: () => this.deleteAddress(index),
    });
  }

  /**
   * Al presionar el botón de guardar dirección.
   */
  onCreateAddressSubmit(): void {
    this.addressFormElement.nativeElement.dispatchEvent(
      new Event('submit', { cancelable: true })
    );
    if (!this.addressForm.valid) {
      // Indicar error al crear dirección.
      return;
    }
    const address = this.addressForm.getRawValue(); // as AddressForm;
    this.createAddress(address);
  }

  /**
   * Crear dirección de despacho en el cliente o crear invitado incluyendo ya la dirección.
   * @param formData
   */
  private createAddress(formData: any): void {
    this.blockUiService.set(true);
    let city: string = '';
    let province: string = '';
    let region: string = '';
    if (this.countryCode === 'cl')
      [city, province, region] = formData.commune.split('@');
    else if (this.countryCode === 'pe') {
      // En perú creo que solo es necesario el city.
      city = formData.locality;
      province = formData.region;
      region = formData.region;
    }
    const address = {
      documentId: this.session.documentId,
      addressType: AddressType.DELIVERY,
      region,
      city,
      number: formData.number.toString(),
      province,
      street: formData.street,
      location: city,
      latitude: formData.latitude,
      longitude: formData.longitude,
      departmentOrHouse:
        this.countryCode === 'cl' ? formData.departmentOrHouse : region,
      reference: formData.reference,
    };
    //guardar dirección para invitado
    if (!this.sessionService.isLoggedIn()) {
      const addressSplit = address.region.split('0');
      const regionCode =
        addressSplit.length > 1 ? addressSplit[1] : address.region;
      const newAddress: ICustomerAddress = {
        id: '0000', // para invitado solamente
        address:
          this.countryCode === 'cl'
            ? `${address.street} ${address.number}, ${address.city}`
            : `${address.street} ${address.number}`,
        city: address.city,
        country: '',
        departmentHouse: address.departmentOrHouse,
        lat: address.latitude,
        lng: address.longitude,
        number: address.number,
        province: address.province,
        region: address.region,
        regionCode:
          this.countryCode === 'cl'
            ? addressSplit.length > 1
              ? addressSplit[1]
              : address.region
            : formData.department,
        reference: address.reference,
        street: address.street,
        location: address.location,
        type: 'DELIVERY',
        typeCode: 2,
      };

      //this.checkoutStorageService.setDeliveryAddress(newAddress);

      //endpoint que setea la dirección para invitado

      // this.customerAddressService.setCustomerAddress(newAddress);
      // Ya no se crea invitado...
      /*const guestFormData = this.customerService.getFormData();
      const formmatedPhone = `${environment.phone.code}${guestFormData.phone}`;
      const createGuest: CreateGuestRequestDto = {
        documentId: guestFormData.documentId,
        email: guestFormData.email,
        firstName: guestFormData.firstName,
        lastName: guestFormData.lastName,
        phone: formmatedPhone,
        address: {
          number: formData.number,
          street: formData.street,
          reference: formData.reference,
          latitude: formData.latitude,
          longitude: formData.longitude,
          // Corregir
          location: formData.location,
          city, //newAddress.city,
          region, //newAddress.region,
          province, //newAddress.province,
          departmentOrHouse: address.departmentOrHouse, //newAddress.departmentHouse,
        },
      };
      //esto esta sacado del componente original de direccion-despacho
      this.customerApiService.createGuestUser(createGuest).subscribe({
        next: ({ addresses }) => {
          //
          const addressOptions = getAddressOptions(addresses, addresses[0].id);
          const selectedAddress = addressOptions.find(
            (address) => address.isDefault
          );
          if (selectedAddress) {
            this.customerAddressService.setCustomerAddress(selectedAddress);
            this.addresses.unshift(selectedAddress);
            this.selectedAddressForm.setValue({
              id: selectedAddress.id,
            });
          }
          this.showCreateAddressSuccess();
          this.blockUiService.set(false);
          this.selectAddressSubmit();
        },
        error: () => {
          this.showCreateAddressError();
          this.blockUiService.set(false);
        },
      });*/

      const addresses: ICustomerAddress[] = [newAddress];

      const addressOptions = getAddressOptions(addresses, addresses[0].id);
      const selectedAddress = addressOptions.find(
        (address) => address.isDefault
      );
      if (selectedAddress) {
        this.addresses.unshift(selectedAddress);
        this.selectedAddressForm.setValue({
          id: selectedAddress.id,
        });
      }
      this.showCreateAddressSuccess();
      this.blockUiService.set(false);
      this.selectAddressSubmit();
    } else {
      // Guardar dirección cuando esta logueado.
      this.customerAddressApiService.createAddress(address).subscribe({
        next: (newAddress: any) => {
          this.showCreateAddressSuccess();
          if (this.addresses.length > 1)
            this.addresses.splice(1, 0, newAddress);
          else this.addresses.push(newAddress);
          this.step.set(1);
          this.addressForm.reset();
          this.blockUiService.set(false);
        },
        error: () => {
          this.showCreateAddressError();
          this.blockUiService.set(false);
        },
      });
    }
  }

  /**
   * Dispara el evento submit del formulario de dirección.
   */
  onSubmitAddressForm(): void {
    if (!this.addressForm.valid) this.addressForm.markAllAsTouched();
  }

  private showCreateAddressError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error creando dirección',
      detail: 'Ha ocurrido un error creando la dirección.',
    });
  }

  /**
   * Mostrar mensaje de dirección creada.
   */
  private showCreateAddressSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Dirección creada',
      detail: 'La dirección ha sido creada correctamente.',
    });
  }

  /**
   * Guardar dirección seleccionada.
   */
  selectAddressSubmit(): void {
    this.visible = false;
    const selectedAddressId = this.idField?.value;
    if (!selectedAddressId) {
      // Mostrar error de dirección no seleccionada.
      return;
    }
    const selectedAddress = this.addresses.find(
      (address) => address.id === selectedAddressId
    );
    if (!selectedAddress) {
      // Mostrar error de dirección no encontrada.
      return;
    }
    this.onSubmit.emit(selectedAddress);
  }
}
